/**
 * SoccerGoalSvg — Professional dual-view soccer shot analytics.
 *
 * Section A (top): 9-zone goal face (3×3 grid).
 *   Corner zones convert at 4-5× the rate of center zones.
 *   Based on StatsBomb / Opta / ASA shot placement research.
 *
 * Section B (bottom): Half-pitch xG shot map.
 *   Bubble markers at kicker positions — size = xG, fill = outcome.
 *   Field lines: goal, 6-yard box, penalty area, D-arc.
 *
 * Real dimensions: Goal 7.32m × 2.44m, PA 40.32m × 16.5m.
 */

import styles from "./SportSvg.module.css";

/* ═══════ GOAL FACE (9-zone) ═══════ */
const GF_X = 100;
const GF_Y = 25;
const GF_W = 300;
const GF_H = 114; /* 3 rows × 38px */
const GF_COL = GF_W / 3;
const GF_ROW = GF_H / 3;

/* 9-zone conversion rates: corners >> center (StatsBomb/ASA data) */
const GOAL_ZONES = [
  /* Top row — near crossbar (hardest for GK) */
  { id: "tl", c: 0, r: 0, pct: 56, color: "#34c759" },
  { id: "tc", c: 1, r: 0, pct: 18, color: "#ff9500" },
  { id: "tr", c: 2, r: 0, pct: 52, color: "#34c759" },
  /* Mid row */
  { id: "ml", c: 0, r: 1, pct: 26, color: "#66bb6a" },
  { id: "mc", c: 1, r: 1, pct: 13, color: "#ff3b30" },
  { id: "mr", c: 2, r: 1, pct: 24, color: "#ffd740" },
  /* Bottom row — near ground */
  { id: "bl", c: 0, r: 2, pct: 32, color: "#66bb6a" },
  { id: "bc", c: 1, r: 2, pct: 11, color: "#ff3b30" },
  { id: "br", c: 2, r: 2, pct: 30, color: "#66bb6a" },
];

/* ═══════ PITCH SHOT MAP ═══════ */
/* Scale ≈ 8px/m, center at x=250, goal line at PGL */
const PGL = 215;
const PCX = 250;

/* Field geometry (8px/m) */
const GP_L = 221; /* goal post left  */
const GP_R = 279; /* goal post right */
const SYB = { l: 177, r: 323, d: 44 }; /* 6-yard box */
const PA = { l: 89, r: 411, d: 132 }; /* penalty area */
const PK_Y = PGL + 88; /* penalty spot */
/* D-arc: 9.15m radius from PK spot, only outside PA */
const DARC_SX = 192;
const DARC_EX = 308;
const DARC_R = 73;

/* xG shot positions on pitch */
type Outcome = "goal" | "saved" | "missed";
interface Shot {
  id: string;
  x: number;
  y: number;
  xg: number;
  out: Outcome;
}

const SHOTS: Shot[] = [
  /* Inside 6-yard box — close range */
  { id: "s1", x: 242, y: PGL + 18, xg: 0.72, out: "goal" },
  /* Header inside box */
  { id: "s2", x: 292, y: PGL + 48, xg: 0.38, out: "goal" },
  /* Penalty kick */
  { id: "s3", x: PCX, y: PK_Y, xg: 0.76, out: "goal" },
  /* Inside box — left channel */
  { id: "s4", x: 158, y: PGL + 78, xg: 0.14, out: "saved" },
  /* Inside box — right */
  { id: "s5", x: 352, y: PGL + 92, xg: 0.09, out: "saved" },
  /* Edge of box — center */
  { id: "s6", x: 270, y: PGL + 128, xg: 0.12, out: "goal" },
  /* Tight angle — left wing */
  { id: "s7", x: 105, y: PGL + 62, xg: 0.03, out: "missed" },
  /* Outside box — center-left */
  { id: "s8", x: 218, y: PGL + 178, xg: 0.04, out: "missed" },
  /* Long range — right */
  { id: "s9", x: 332, y: PGL + 205, xg: 0.03, out: "saved" },
  /* Free kick */
  { id: "s10", x: PCX, y: PGL + 158, xg: 0.06, out: "goal" },
  /* Wide angle — right wing */
  { id: "s11", x: 398, y: PGL + 108, xg: 0.04, out: "missed" },
  /* Inside box — central (saved) */
  { id: "s12", x: 258, y: PGL + 62, xg: 0.45, out: "saved" },
];

function xgRadius(xg: number): number {
  return 4 + xg * 12;
}

const OUT_FILL: Record<Outcome, string> = {
  goal: "#34c759",
  saved: "#ffd740",
  missed: "none",
};
const OUT_STROKE: Record<Outcome, string> = {
  goal: "#34c759",
  saved: "#ffd740",
  missed: "#ff3b30",
};

export function SoccerGoalSvg() {
  return (
    <svg
      viewBox="0 0 500 700"
      className={styles.svg}
      role="img"
      aria-label="Soccer shot analytics — 9-zone goal face and half-pitch xG shot map"
    >
      <title>Soccer shot analytics — 9-zone conversion and pitch xG map</title>
      <defs>
        <pattern id="sg-net" x="0" y="0" width="12" height="12" patternUnits="userSpaceOnUse">
          <line x1="0" y1="0" x2="12" y2="12" stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" />
          <line x1="12" y1="0" x2="0" y2="12" stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" />
        </pattern>
        <pattern id="sg-pitch" x="0" y="0" width="36" height="36" patternUnits="userSpaceOnUse">
          <rect width="36" height="36" fill="rgba(76,175,80,0.03)" />
          <rect x="0" y="0" width="18" height="36" fill="rgba(76,175,80,0.015)" />
        </pattern>
        <linearGradient id="sg-xg-bg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(0,100,60,0.06)" />
          <stop offset="100%" stopColor="rgba(0,100,60,0.02)" />
        </linearGradient>
      </defs>

      {/* ════════ SECTION A: Goal Face 9-Zone ════════ */}
      <text
        x="250"
        y="15"
        textAnchor="middle"
        className={styles.zoneLabel}
        fill="rgba(255,255,255,0.3)"
      >
        GOAL FACE — 9-ZONE CONVERSION
      </text>

      {/* Net background */}
      <rect x={GF_X} y={GF_Y} width={GF_W} height={GF_H} fill="url(#sg-net)" />

      {/* Net depth (3D perspective) */}
      <line
        x1={GF_X}
        y1={GF_Y}
        x2={GF_X - 12}
        y2={GF_Y - 10}
        stroke="rgba(255,255,255,0.12)"
        strokeWidth="1"
      />
      <line
        x1={GF_X + GF_W}
        y1={GF_Y}
        x2={GF_X + GF_W + 12}
        y2={GF_Y - 10}
        stroke="rgba(255,255,255,0.12)"
        strokeWidth="1"
      />
      <line
        x1={GF_X - 12}
        y1={GF_Y - 10}
        x2={GF_X + GF_W + 12}
        y2={GF_Y - 10}
        stroke="rgba(255,255,255,0.08)"
        strokeWidth="1"
      />

      {/* ── 9-zone fills + percentages ── */}
      {GOAL_ZONES.map((z) => {
        const x = GF_X + z.c * GF_COL;
        const y = GF_Y + z.r * GF_ROW;
        const isCorner = (z.c === 0 || z.c === 2) && (z.r === 0 || z.r === 2);
        return (
          <g key={z.id}>
            <rect
              x={x + 1}
              y={y + 1}
              width={GF_COL - 2}
              height={GF_ROW - 2}
              rx="2"
              fill={`${z.color}${isCorner ? "1A" : "0E"}`}
              stroke={`${z.color}${isCorner ? "40" : "20"}`}
              strokeWidth={isCorner ? 1.5 : 1}
            />
            <text
              x={x + GF_COL / 2}
              y={y + GF_ROW / 2 - 1}
              textAnchor="middle"
              dominantBaseline="central"
              className={styles.zonePct}
              fill={z.color}
            >
              {z.pct}%
            </text>
            <text
              x={x + GF_COL / 2}
              y={y + GF_ROW / 2 + 13}
              textAnchor="middle"
              className={styles.zoneLabel}
              fill="rgba(255,255,255,0.2)"
            >
              {z.id.toUpperCase()}
            </text>
          </g>
        );
      })}

      {/* Zone grid dividers */}
      <line
        x1={GF_X + GF_COL}
        y1={GF_Y}
        x2={GF_X + GF_COL}
        y2={GF_Y + GF_H}
        stroke="rgba(255,255,255,0.1)"
        strokeWidth="1"
      />
      <line
        x1={GF_X + GF_COL * 2}
        y1={GF_Y}
        x2={GF_X + GF_COL * 2}
        y2={GF_Y + GF_H}
        stroke="rgba(255,255,255,0.1)"
        strokeWidth="1"
      />
      <line
        x1={GF_X}
        y1={GF_Y + GF_ROW}
        x2={GF_X + GF_W}
        y2={GF_Y + GF_ROW}
        stroke="rgba(255,255,255,0.1)"
        strokeWidth="1"
      />
      <line
        x1={GF_X}
        y1={GF_Y + GF_ROW * 2}
        x2={GF_X + GF_W}
        y2={GF_Y + GF_ROW * 2}
        stroke="rgba(255,255,255,0.1)"
        strokeWidth="1"
      />

      {/* Goal frame */}
      <rect
        x={GF_X - 3}
        y={GF_Y - 5}
        width={GF_W + 6}
        height="7"
        rx="3.5"
        fill="rgba(255,255,255,0.6)"
      />
      <rect
        x={GF_X - 5}
        y={GF_Y - 2}
        width="7"
        height={GF_H + 5}
        rx="3.5"
        fill="rgba(255,255,255,0.55)"
      />
      <rect
        x={GF_X + GF_W - 2}
        y={GF_Y - 2}
        width="7"
        height={GF_H + 5}
        rx="3.5"
        fill="rgba(255,255,255,0.55)"
      />
      <line
        x1={GF_X - 10}
        y1={GF_Y + GF_H + 2}
        x2={GF_X + GF_W + 10}
        y2={GF_Y + GF_H + 2}
        stroke="rgba(255,255,255,0.35)"
        strokeWidth="2"
      />

      {/* Row labels */}
      <text
        x={GF_X - 18}
        y={GF_Y + GF_ROW * 0.5 + 2}
        textAnchor="middle"
        className={styles.zoneLabel}
        fill="rgba(255,255,255,0.25)"
      >
        Top
      </text>
      <text
        x={GF_X - 18}
        y={GF_Y + GF_ROW * 1.5 + 2}
        textAnchor="middle"
        className={styles.zoneLabel}
        fill="rgba(255,255,255,0.25)"
      >
        Mid
      </text>
      <text
        x={GF_X - 18}
        y={GF_Y + GF_ROW * 2.5 + 2}
        textAnchor="middle"
        className={styles.zoneLabel}
        fill="rgba(255,255,255,0.25)"
      >
        Low
      </text>

      {/* Shot dots on goal face */}
      <circle cx={GF_X + 22} cy={GF_Y + 12} r="4" fill="#34c759" opacity="0.9" />
      <circle cx={GF_X + GF_W - 18} cy={GF_Y + 14} r="4" fill="#34c759" opacity="0.9" />
      <circle cx={GF_X + GF_COL + 50} cy={GF_Y + GF_ROW + 12} r="4" fill="#ffd740" opacity="0.8" />
      <circle cx={GF_X + 15} cy={GF_Y + GF_ROW * 2 + 10} r="4" fill="#66bb6a" opacity="0.85" />
      <circle
        cx={GF_X + GF_COL + 30}
        cy={GF_Y + GF_ROW * 2 + 15}
        r="4"
        fill="none"
        stroke="#ff3b30"
        strokeWidth="1.5"
        opacity="0.75"
      />

      {/* GK figure */}
      <g>
        <circle
          cx="250"
          cy={GF_Y + GF_H - 14}
          r="7"
          fill="rgba(255,149,0,0.15)"
          stroke="#ff9500"
          strokeWidth="1.5"
        />
        <line
          x1="240"
          y1={GF_Y + GF_H - 16}
          x2="228"
          y2={GF_Y + GF_H - 24}
          stroke="#ff9500"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <line
          x1="260"
          y1={GF_Y + GF_H - 16}
          x2="272"
          y2={GF_Y + GF_H - 24}
          stroke="#ff9500"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <text
          x="250"
          y={GF_Y + GF_H + 14}
          textAnchor="middle"
          className={styles.zoneLabel}
          fill="#ff9500"
        >
          GK
        </text>
      </g>

      {/* Corner emphasis markers */}
      <text
        x={GF_X + GF_COL / 2}
        y={GF_Y + GF_H + 28}
        textAnchor="middle"
        className={styles.zoneLabel}
        fill="rgba(52,199,89,0.5)"
      >
        Corner
      </text>
      <text
        x={GF_X + GF_COL * 2.5}
        y={GF_Y + GF_H + 28}
        textAnchor="middle"
        className={styles.zoneLabel}
        fill="rgba(52,199,89,0.5)"
      >
        Corner
      </text>

      {/* Goal face legend */}
      <g>
        <text x="16" y={GF_Y + 8} className={styles.zoneLabel} fill="rgba(255,255,255,0.3)">
          Conv %
        </text>
        <rect x="12" y={GF_Y + 14} width="7" height="7" rx="1" fill="#34c759" opacity="0.5" />
        <text x="23" y={GF_Y + 21} className={styles.zoneLabel} fill="rgba(255,255,255,0.25)">
          50%+
        </text>
        <rect x="12" y={GF_Y + 27} width="7" height="7" rx="1" fill="#66bb6a" opacity="0.5" />
        <text x="23" y={GF_Y + 34} className={styles.zoneLabel} fill="rgba(255,255,255,0.25)">
          25-49
        </text>
        <rect x="12" y={GF_Y + 40} width="7" height="7" rx="1" fill="#ffd740" opacity="0.5" />
        <text x="23" y={GF_Y + 47} className={styles.zoneLabel} fill="rgba(255,255,255,0.25)">
          15-24
        </text>
        <rect x="12" y={GF_Y + 53} width="7" height="7" rx="1" fill="#ff3b30" opacity="0.5" />
        <text x="23" y={GF_Y + 60} className={styles.zoneLabel} fill="rgba(255,255,255,0.25)">
          {"<15"}
        </text>
      </g>

      {/* ════════ Divider ════════ */}
      <line x1="50" y1="185" x2="450" y2="185" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />

      {/* ════════ SECTION B: Half-Pitch xG Shot Map ════════ */}
      <text
        x="250"
        y="205"
        textAnchor="middle"
        className={styles.zoneLabel}
        fill="rgba(255,255,255,0.3)"
      >
        SHOT MAP — KICKER POSITION (xG)
      </text>

      {/* Pitch surface */}
      <rect x="30" y={PGL - 5} width="440" height="480" rx="3" fill="url(#sg-xg-bg)" />
      <rect x="30" y={PGL - 5} width="440" height="480" rx="3" fill="url(#sg-pitch)" />

      {/* ── Goal line + posts ── */}
      <line x1="30" y1={PGL} x2="470" y2={PGL} stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" />
      <line
        x1={GP_L}
        y1={PGL - 5}
        x2={GP_L}
        y2={PGL + 3}
        stroke="rgba(255,255,255,0.6)"
        strokeWidth="3"
      />
      <line
        x1={GP_R}
        y1={PGL - 5}
        x2={GP_R}
        y2={PGL + 3}
        stroke="rgba(255,255,255,0.6)"
        strokeWidth="3"
      />
      <line
        x1={GP_L}
        y1={PGL - 4}
        x2={GP_R}
        y2={PGL - 4}
        stroke="rgba(255,255,255,0.5)"
        strokeWidth="2"
      />
      <text
        x={PCX}
        y={PGL - 10}
        textAnchor="middle"
        className={styles.zoneLabel}
        fill="rgba(255,255,255,0.35)"
      >
        GOAL (7.32m)
      </text>

      {/* ── 6-yard box ── */}
      <rect
        x={SYB.l}
        y={PGL}
        width={SYB.r - SYB.l}
        height={SYB.d}
        fill="none"
        stroke="rgba(255,255,255,0.15)"
        strokeWidth="1.5"
      />
      <text
        x={PCX}
        y={PGL + SYB.d + 12}
        textAnchor="middle"
        className={styles.zoneLabel}
        fill="rgba(255,255,255,0.15)"
      >
        6-yard
      </text>

      {/* ── Penalty area ── */}
      <rect
        x={PA.l}
        y={PGL}
        width={PA.r - PA.l}
        height={PA.d}
        fill="none"
        stroke="rgba(255,255,255,0.12)"
        strokeWidth="1.5"
      />

      {/* Penalty spot */}
      <circle cx={PCX} cy={PK_Y} r="3" fill="rgba(255,255,255,0.5)" />
      <text x={PCX + 18} y={PK_Y + 4} className={styles.zoneLabel} fill="rgba(255,255,255,0.25)">
        PK (11m)
      </text>

      {/* D-arc (outside penalty area only) */}
      <path
        d={`M ${DARC_SX},${PGL + PA.d} A ${DARC_R},${DARC_R} 0 0,1 ${DARC_EX},${PGL + PA.d}`}
        fill="none"
        stroke="rgba(255,255,255,0.1)"
        strokeWidth="1"
        strokeDasharray="4,4"
      />

      {/* Penalty area label (rotated) */}
      <text
        x={PA.r + 12}
        y={PGL + PA.d / 2}
        className={styles.zoneLabel}
        fill="rgba(255,255,255,0.12)"
        transform={`rotate(90, ${PA.r + 12}, ${PGL + PA.d / 2})`}
      >
        Penalty Area (16.5m)
      </text>

      {/* Distance markers */}
      <line
        x1="42"
        y1={PGL + 80}
        x2="54"
        y2={PGL + 80}
        stroke="rgba(255,255,255,0.12)"
        strokeWidth="1"
      />
      <text x="58" y={PGL + 83} className={styles.zoneLabel} fill="rgba(255,255,255,0.12)">
        10m
      </text>
      <line
        x1="42"
        y1={PGL + 160}
        x2="54"
        y2={PGL + 160}
        stroke="rgba(255,255,255,0.12)"
        strokeWidth="1"
      />
      <text x="58" y={PGL + 163} className={styles.zoneLabel} fill="rgba(255,255,255,0.12)">
        20m
      </text>
      <line
        x1="42"
        y1={PGL + 240}
        x2="54"
        y2={PGL + 240}
        stroke="rgba(255,255,255,0.12)"
        strokeWidth="1"
      />
      <text x="58" y={PGL + 243} className={styles.zoneLabel} fill="rgba(255,255,255,0.12)">
        30m
      </text>

      {/* ── xG zone shading ── */}
      <text
        x={PCX}
        y={PGL + 34}
        textAnchor="middle"
        className={styles.zoneLabel}
        fill="rgba(255,255,255,0.1)"
      >
        HIGH xG ZONE
      </text>
      <text
        x={PCX}
        y={PGL + PA.d - 5}
        textAnchor="middle"
        className={styles.zoneLabel}
        fill="rgba(255,255,255,0.08)"
      >
        MEDIUM xG
      </text>
      <text
        x={PCX}
        y={PGL + 195}
        textAnchor="middle"
        className={styles.zoneLabel}
        fill="rgba(255,255,255,0.06)"
      >
        LOW xG
      </text>

      {/* ── xG Shot markers ── */}
      {SHOTS.map((s) => {
        const r = xgRadius(s.xg);
        return (
          <g key={s.id}>
            {s.out === "goal" && (
              <circle cx={s.x} cy={s.y} r={r + 3} fill="#34c759" opacity="0.1" />
            )}
            <circle
              cx={s.x}
              cy={s.y}
              r={r}
              fill={OUT_FILL[s.out]}
              stroke={OUT_STROKE[s.out]}
              strokeWidth={s.out === "missed" ? 1.5 : 0}
              opacity={s.out === "missed" ? 0.7 : 0.85}
            />
            {s.xg >= 0.1 && (
              <text
                x={s.x}
                y={s.y + r + 10}
                textAnchor="middle"
                className={styles.zoneLabel}
                fill="rgba(255,255,255,0.22)"
              >
                {s.xg.toFixed(2)}
              </text>
            )}
          </g>
        );
      })}

      {/* ── Shot trajectories (notable goals → goal line) ── */}
      {/* Close range goal → top-left corner */}
      <line
        x1={242}
        y1={PGL + 18}
        x2={GP_L + 8}
        y2={PGL}
        stroke="rgba(52,199,89,0.2)"
        strokeWidth="1.5"
        strokeDasharray="5,3"
      />
      {/* Edge of box goal → bottom-right */}
      <line
        x1={270}
        y1={PGL + 128}
        x2={GP_R - 12}
        y2={PGL}
        stroke="rgba(52,199,89,0.15)"
        strokeWidth="1"
        strokeDasharray="4,4"
      />
      {/* Free kick goal → top-left post */}
      <line
        x1={PCX}
        y1={PGL + 158}
        x2={GP_L + 4}
        y2={PGL}
        stroke="rgba(52,199,89,0.12)"
        strokeWidth="1"
        strokeDasharray="4,4"
      />

      {/* ── Shot map legend ── */}
      <g>
        <text x="16" y={PGL + 5} className={styles.zoneLabel} fill="rgba(255,255,255,0.3)">
          Shots
        </text>
        <circle cx="16" cy={PGL + 16} r="4" fill="#34c759" opacity="0.8" />
        <text x="25" y={PGL + 19} className={styles.zoneLabel} fill="rgba(255,255,255,0.25)">
          Goal
        </text>
        <circle cx="16" cy={PGL + 30} r="4" fill="#ffd740" opacity="0.8" />
        <text x="25" y={PGL + 33} className={styles.zoneLabel} fill="rgba(255,255,255,0.25)">
          Saved
        </text>
        <circle
          cx="16"
          cy={PGL + 44}
          r="4"
          fill="none"
          stroke="#ff3b30"
          strokeWidth="1.5"
          opacity="0.7"
        />
        <text x="25" y={PGL + 47} className={styles.zoneLabel} fill="rgba(255,255,255,0.25)">
          Missed
        </text>
        <text x="16" y={PGL + 64} className={styles.zoneLabel} fill="rgba(255,255,255,0.18)">
          Size = xG
        </text>
      </g>
    </svg>
  );
}
